/**
 * Comments on a task or an idea (FEAT-024, addendum §2 and §3).
 *
 * Deliberately NOT wired to Telegram's MainButton: on a task that button is
 * already the journal / completion action, and a screen where the primary
 * control silently changes meaning depending on which field has focus is a
 * screen you cannot trust. So the composer carries its own compact primary,
 * exactly as the addendum specifies.
 *
 * Own comments can be edited and deleted (ghost actions, §7); other people's
 * are read-only — the API enforces the same rule, this just stops offering an
 * action that would 403.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Comments, type CommentPatch } from "@/api/resources";
import { ChatThread, editPatch, MessageText } from "@/components/ChatThread";
import type { CommentOut, CommentParentType } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Section } from "@/components/ui/Section";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";
import { formatRelative, parseDate } from "@/lib/format";
import { nameOf, usePeople } from "@/lib/people";

export interface CommentsSectionProps {
  parentType: CommentParentType;
  parentId: string;
  myUserId?: string;
  /**
   * §3 — under an idea the thread sits directly beneath the text with no
   * section heading; on a task it is a titled section like any other.
   */
  compact?: boolean;
}

export function CommentsSection({
  parentType, parentId, myUserId, compact = false,
}: CommentsSectionProps) {
  if (parentType !== "note") {
    const body = <ChatThread key={`${parentType}-${parentId}`} context={{ parent_type: parentType, parent_id: parentId }} />;
    return compact ? body : <Section title={parentType === "idea" ? "Обсуждение идеи" : "Обсуждение задачи"}>{body}</Section>;
  }
  return <PrivateNoteComments parentType={parentType} parentId={parentId} myUserId={myUserId} compact={compact} />;
}

function PrivateNoteComments({
  parentType,
  parentId,
  myUserId,
  compact = false,
}: CommentsSectionProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const people = usePeople();
  const key = ["comments", parentType, parentId];

  const thread = useQuery({
    queryKey: key,
    queryFn: () => Comments.list(parentType, parentId),
    enabled: Boolean(parentId),
    refetchInterval: 15_000,
  });

  const [draft, setDraft] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: (body_md: string) => Comments.create({ parent_type: parentType, parent_id: parentId, body_md }),
    onSuccess: () => {
      setDraft("");
      invalidate();
    },
    onError: () => toast.error("Не удалось отправить комментарий"),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CommentPatch }) => Comments.update(id, patch),
    onSuccess: invalidate,
    onError: () => toast.error("Не удалось сохранить правку"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => Comments.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("Комментарий удалён");
    },
    onError: () => toast.error("Не удалось удалить комментарий"),
  });

  const items = thread.data ?? [];
  const canSend = draft.trim().length > 0 && !create.isPending;

  const body = (
    <>
      {thread.isLoading ? (
        <SkeletonBlock className="h-16" />
      ) : (
        items.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            author={nameOf(people.data, c.author_id)}
            mine={Boolean(myUserId) && c.author_id === myUserId}
            onSave={(patch) => update.mutateAsync({ id: c.id, patch })}
            onDelete={() => remove.mutate(c.id)}
            busy={update.isPending || remove.isPending}
          />
        ))
      )}

      {thread.isError && <div role="alert" className="text-meta text-dstr">Не удалось обновить обсуждение. <Button size="compact" variant="ghost" onClick={() => void thread.refetch()}>Повторить</Button></div>}
      {!thread.isLoading && !thread.isError && items.length === 0 && (
        <p className="text-meta text-hint">Обсуждения пока нет.</p>
      )}

      <div className="flex flex-col gap-2">
        <TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Написать комментарий…"
          aria-label="Новый комментарий"
          disabled={create.isPending}
        />
        <div className="flex justify-end">
          <Button
            size="compact"
            onClick={() => create.mutate(draft.trim())}
            disabled={!canSend}
            loading={create.isPending}
            loadingText="Отправляем…"
          >
            Отправить
          </Button>
        </div>
      </div>
    </>
  );

  if (compact) {
    return <div className="flex flex-col gap-2">{body}</div>;
  }
  return (
    <Section title={parentType === "task" ? "Обсуждение задачи" : "Комментарии"} trailing={items.length > 0 ? <span className="text-section text-hint">{items.length}</span> : undefined}>
      {body}
    </Section>
  );
}

function CommentRow({
  comment,
  author,
  mine,
  onSave,
  onDelete,
  busy,
}: {
  comment: CommentOut;
  author: string | null;
  mine: boolean;
  onSave: (patch: CommentPatch) => Promise<unknown>;
  onDelete: () => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(comment.body_md);
  const [summary, setSummary] = useState(comment.summary_md ?? "");
  const [full, setFull] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const patch = editPatch(comment, value, summary);

  const when = parseDate(comment.created_at);
  const edited = comment.edited_at != null;

  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="truncate-line text-meta font-medium text-text">
          {author ?? "Кто-то"}
        </span>
        <span className="shrink-0 text-chip text-hint">
          {when ? formatRelative(when) : ""}
          {edited && " · изменён"}
        </span>
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          {comment.summary_md != null && (
            <TextArea
              label="Кратко"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={600}
              disabled={busy}
            />
          )}
          <TextArea
            label={comment.summary_md != null ? "Полностью" : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="Текст комментария"
            disabled={busy}
          />
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="compact"
              onClick={() => {
                setValue(comment.body_md);
                setSummary(comment.summary_md ?? "");
                setEditing(false);
              }}
            >
              Отмена
            </Button>
            <Button
              size="compact"
              disabled={!value.trim() || !Object.keys(patch).length}
              loading={busy}
              loadingText="Сохраняем…"
              onClick={async () => {
                try {
                  await onSave(patch);
                  setEditing(false);
                } catch { /* mutation shows an error; retain the text for retry */ }
              }}
            >
              Сохранить
            </Button>
          </div>
        </div>
      ) : comment.summary_md ? (
        // Spec 016: the short version first; the header above already says «изменён».
        <MessageText message={{ ...comment, edited_at: null }} full={full} onToggle={() => setFull(!full)} />
      ) : (
        <p className="whitespace-pre-wrap break-words text-body text-text">{comment.body_md}</p>
      )}

      {mine && !editing && (
        <div className="flex justify-end gap-1">
          {confirmingDelete ? (
            <>
              {/*
                A destructive action gets a second tap, not a native confirm():
                the spec has no dialog primitive and window.confirm looks
                nothing like the product.
              */}
              <Button variant="ghost" size="compact" onClick={() => setConfirmingDelete(false)}>
                Отмена
              </Button>
              <Button
                variant="destructive-outline"
                size="compact"
                loading={busy}
                onClick={onDelete}
              >
                Точно удалить
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="compact" onClick={() => { setValue(comment.body_md); setSummary(comment.summary_md ?? ""); setEditing(true); }}>
                Изменить
              </Button>
              <Button
                variant="ghost"
                size="compact"
                className={cn("text-dstr")}
                onClick={() => setConfirmingDelete(true)}
              >
                Удалить
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
